import { Button, Tooltip } from "antd";
import { FaCheckCircle, FaClone, FaExclamation, FaTags, FaGift, FaInfoCircle } from "react-icons/fa";
import useAdBlockStore from "store/AdBlockStore";
import { offerStringEval, incentiveStringEval } from "utils"
import useKeypress from "react-use-keypress";
import { DeleteConfirmation } from "../DeleteConfirmation";
import { BsClockFill } from "react-icons/bs";

export const OfferPreview = () => {
    const isLoading = useAdBlockStore((state) => state.isLoading);
    const isApproving = useAdBlockStore(state => state.isApproving);
    const isRejecting = useAdBlockStore(state => state.isRejecting);
    const isDNELoading = useAdBlockStore(state => state.isDNELoading);
    const adblockInfo = useAdBlockStore((state) => state.activeBlockData);
    const offers = useAdBlockStore((state) => state.offers);
    const incentives = useAdBlockStore((state) => state.incentives);
    const selectedRow = useAdBlockStore((state) => state.currentOffer);
    const setCurrentOffer = useAdBlockStore((state) => state.setCurrentOffer);
    const currentItemType = useAdBlockStore((state) => state.currentItemType);

    useKeypress([']'], (event) => {
        event.preventDefault();
        if (selectedRow?.index === (offers.length + incentives.length - 1)) {
            setCurrentOffer(offers[0]);
            return;
        }
        const nextIndex = (selectedRow?.index || 0) + 1;
        const nextOffer = offers.find((p) => p.index === nextIndex);
        const nextIncentive = incentives.find((p) => p.index === nextIndex);
        if (nextOffer || nextIncentive) {
            setCurrentOffer(nextOffer || nextIncentive);
        }
    });

    useKeypress(['['], (event) => {
        event.preventDefault();
        if (selectedRow?.index === 0) {
            if (incentives.length > 0) {
                setCurrentOffer(incentives[incentives.length - 1]);
                return;
            }
            setCurrentOffer(offers[offers.length - 1]);
            return;
        }
        const prevIndex = (selectedRow?.index || 0) - 1;
        const prevOffer = offers.find((p) => p.index === prevIndex);
        const prevIncentive = incentives.find((p) => p.index === prevIndex);
        if (prevOffer || prevIncentive) {
            setCurrentOffer(prevOffer || prevIncentive);
        }
    });

    // Clone offers
    useKeypress(["o", "O"], (event) => {
        // Ignore OS key-repeat: holding Alt+O must not clone a row per repeat.
        if (event.repeat) return;
        if (event.altKey) {
            if (!(isLoading || isApproving || isRejecting || isDNELoading)) {
                if (currentItemType === 'offer' && offers.length > 0) {
                    useAdBlockStore.getState().cloneOffer(selectedRow.index);
                } else if (currentItemType === 'incentive' && incentives.length > 0) {
                    useAdBlockStore.getState().cloneIncentive(Math.abs(offers.length - selectedRow.index));
                }
            }
        }
    });

    return (
        <>
            {offers.map((offer, offerIdx) => (
                <div
                    key={offerIdx}
                    onClick={() => useAdBlockStore.setState({ currentOffer: offer, currentItemType: "offer" })}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "6px 10px",
                        cursor: "pointer",
                        borderRadius: "4px",
                        border: `2px solid ${selectedRow?.index === offer.index ? "#1890ff" : "var(--gray-color)"}`,
                        backgroundColor: selectedRow?.index === offer.index ? "var(--items-color)" : "transparent",
                        fontSize: "15px",
                        fontWeight: "500",
                        letterSpacing: "0.4px",
                        transition: "all 0.2s ease"
                    }}
                >

                    <Button type="link" size="small" style={{ padding: 0 }} color="red">
                        {offer.isValid ?
                            <FaCheckCircle color="var(--success-color)" />
                            : <FaExclamation color="var(--error-color)" />}
                    </Button>

                    <FaTags className="mx-2" color="white" />

                    {offerStringEval(offer, offerIdx)}

                    <div style={{
                        display: "flex",
                        gap: "8px",
                        color: "rgba(255, 255, 255, 0.6)",
                        fontSize: "11px",
                    }}>
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0 }}
                            color="red">

                        </Button>
                        {offer.existingOffer &&
                            <Button
                            type="link"
                            size="small"
                                style={{ padding: 0 }}
                                color="red"
                                onClick={() => window.open(`/offer/${adblockInfo.circular_uuid}/${adblockInfo.page_uuid}/${adblockInfo.uuid}`, "_blank")}>
                                <Tooltip title="Existing Product">
                                    <BsClockFill color="var(--misc-purple)" />
                                </Tooltip>
                            </Button>
                        }
                        <Button type="link" size="small" style={{ padding: 0 }} onClick={(e) => { e.stopPropagation(); useAdBlockStore.getState().cloneOffer(offerIdx) }}>
                            <Tooltip title="Clone Offer">
                                <FaClone color="var(--info-color)" />
                            </Tooltip>
                        </Button>

                        <DeleteConfirmation title="Delete Offer" tooltipText="Delete Offer"
                            description={`Are you sure to delete ${offer.offer_text}? You have to approve the block again.`}
                            data={{ obj: offer, type: 'offer' }}
                        />
                    </div>
                </div>

            ))}
            {incentives.map((incentive, incentiveIdx) => (
                <div
                    key={offers.length + incentiveIdx}
                    onClick={() => useAdBlockStore.setState({ currentOffer: incentive, currentItemType: "incentive" })}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "6px 10px",
                        cursor: "pointer",
                        borderRadius: "4px",
                        border: `2px solid ${selectedRow?.index === incentive.index ? "#1890ff" : "var(--gray-color)"}`,
                        backgroundColor: selectedRow?.index === incentive.index ? "var(--items-color)" : "transparent",
                        fontSize: "14px",
                        transition: "all 0.2s ease"
                    }}
                >

                    <Button type="link" size="small" style={{ padding: 0 }} color="red">
                        {incentive.isValid ?
                            <FaCheckCircle color="var(--success-color)" />
                            : <FaExclamation color="var(--error-color)" />}
                    </Button>

                    <FaGift className="mx-1" />

                    {incentive.existingIncentive && <FaInfoCircle className="mr-1" color="var(--info-color)" onClick={() => window.open(`/offer/${adblockInfo.circular_uuid}/${adblockInfo.page_uuid}/${adblockInfo.uuid}`, "_blank")} />}

                    {incentiveStringEval(incentive)}

                    <div style={{
                        display: "flex",
                        gap: "8px",
                        color: "rgba(255, 255, 255, 0.6)",
                        fontSize: "11px",
                    }}>
                        <Button type="link" size="small" style={{ padding: 0 }} onClick={(e) => { e.stopPropagation(); useAdBlockStore.getState().cloneIncentive(incentiveIdx) }}>
                            <Tooltip title="Clone Incentive">
                                <FaClone color="var(--info-color)" />
                            </Tooltip>
                        </Button>
                        <DeleteConfirmation title="Delete Incentive" tooltipText="Delete Incentive"
                            description={`${['Page', 'Circular'].includes(incentive.incentive_attach_to) && 'Deleting this Incentive will remove them from all associated adblocks.'} Are you sure to delete ${incentive.incentive_text}? You have to approve the block again.`}
                            data={{ obj: incentive, type: 'incentive' }}
                        />
                    </div>
                </div>
            ))}
        </>
    );
};